import type { ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@perch/ui-tokens';

const statusBadgeVariants = cva(
  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border',
  {
    variants: {
      variant: {
        watching: 'bg-[rgba(16,185,129,0.06)] border-[rgba(16,185,129,0.1)] text-emerald-600 dark:text-emerald-400',
        drop: 'bg-[rgba(255,107,53,0.06)] border-[rgba(255,107,53,0.1)] text-[#FF6B35] dark:text-[#FF8A5C]',
        stable: 'bg-[rgba(168,162,158,0.06)] border-[rgba(168,162,158,0.1)] text-stone-500 dark:text-stone-400',
        rebooked: 'bg-[rgba(16,185,129,0.08)] border-[rgba(16,185,129,0.12)] text-emerald-600 dark:text-emerald-400',
        info: 'bg-[rgba(59,130,246,0.06)] border-[rgba(59,130,246,0.1)] text-blue-600 dark:text-blue-400',
        warning: 'bg-[rgba(245,158,11,0.06)] border-[rgba(245,158,11,0.1)] text-amber-600 dark:text-amber-400',
        error: 'bg-[rgba(239,68,68,0.06)] border-[rgba(239,68,68,0.1)] text-red-600 dark:text-red-400',
      },
    },
    defaultVariants: {
      variant: 'stable',
    },
  }
);

interface StatusBadgeProps extends VariantProps<typeof statusBadgeVariants> {
  children: ReactNode;
  dot?: boolean;
  dotPulse?: boolean;
  className?: string;
}

export function StatusBadge({
  children,
  variant,
  dot = false,
  dotPulse = false,
  className,
}: StatusBadgeProps) {
  return (
    <span className={cn(statusBadgeVariants({ variant }), className)}>
      {dot && (
        <span className="relative flex h-1.5 w-1.5">
          {dotPulse && (
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75" />
          )}
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-current" />
        </span>
      )}
      {children}
    </span>
  );
}

export { statusBadgeVariants };
